import { CSSProperties, ReactNode, useCallback, useRef, useState } from 'react';
import styles from './styles.module.css';

type Props = {
  main: ReactNode;
  footer: ReactNode;
  error?: ReactNode;
};

/**
 * ログイン後の画面レイアウトにおいて、共通ヘッダー、共通サイドバー以外の部分のコンテンツで、
 * フッター付きのレイアウトを実現する
 *
 * 以下のスクロールの制約条件を守ってレイアウトを提供する。
 *
 * - 共通ヘッダー・共通サイドバーを除いたエリアをスクロールする
 * - 半透明のフッターの裏にコンテンツが表示される
 * - コンテンツの最下部はフッターの高さの分だけのスペースがある (フッターが手前に重なっているのでスクロールしてコンテンツを上に持って来れるように)
 * - コンテンツの高さが画面高さ未満でも、フッターの位置は画面最下部になる
 * - スクロールバーはフッターより手前にあって操作できる
 * - (main に含まれる想定の) 検索条件部分は横スクロールに対して固定する
 * - (main に含まれる想定の) 表の一行目はスクロール時に共通ヘッダーにぶつかるとそれ以上、上にいかない
 * - (main に含まれる想定の) 表の一列目はスクロール時に共通サイドバーにぶつかるとそれ以上、左にいかない
 * - スクロールバーは常時見えていなければならない (横スクロールバー自体が縦スクロールして隠れてしまわないように)
 * - (main に含まれる想定の) 「検索結果がありません」はフッターを除いたスペースを埋める高さを持つ
 */
function FooterLayout({ main, footer, error }: Props) {
  const { style, setFooter } = useFooterHeight();
  return (
    <div className={styles.container} style={style}>
      <div className={styles.pageContainer}>
        {main}
        <div className={styles.spacer} />
      </div>
      <div className={styles.footer} ref={setFooter}>
        {error && <div className={styles.error}>{error}</div>}
        {footer}
      </div>
    </div>
  );
}

export default FooterLayout;

/**
 * 子孫要素がフッターの高さを取得できるようにするための CSS Variables
 *
 * 「検索結果がありません」は残りの高さを埋める必要がある。ただし、フッターの高さは無視した
 * 計算が必要となるため、フッターの高さを取得しておき差し引く。高さは CSS 変数で伝える。
 */
function useFooterHeight() {
  const [footerHeight, setFooterHeight] = useState(0);

  const observerRef = useRef<ResizeObserver>();
  const setFooter = useCallback((footer: HTMLElement | null) => {
    observerRef.current?.disconnect();

    if (footer) {
      const observer = new ResizeObserver(() => {
        setFooterHeight(footer.clientHeight);
      });
      observer.observe(footer);
      observerRef.current = observer;

      setFooterHeight(footer.clientHeight);
    }
  }, []);

  const style = {
    '--footer-layout-footer-height': `${footerHeight}px`,
  } as CSSProperties;

  return { style, setFooter };
}

/**
 * FooterLayout のフッターの高さを無視して残りの高さを埋めるだけの高さを提供する
 */
export function FooterLayoutCenterize({ children }: { children?: ReactNode }) {
  return (
    <div className={styles.centerizeWrapper}>
      <div className={styles.centerizePadding}>{children}</div>
    </div>
  );
}
